type ValueOf<T> = T[keyof T];

export const APP_ROLE = {
  STUDENT: 'STUDENT',
  TEACHER: 'TEACHER',
  ADMIN: 'ADMIN',
} as const;

export const FEATURE_FLAGS = {
  TIMETABLE: false,
} as const;

export type AppRole = ValueOf<typeof APP_ROLE>;

export const APP_ROLE_OPTIONS: readonly AppRole[] = [
  APP_ROLE.STUDENT,
  APP_ROLE.TEACHER,
  APP_ROLE.ADMIN,
];

export const FORM_MODE = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  CREATE: 'CREATE',
} as const;

export type FormMode = ValueOf<typeof FORM_MODE>;

export const DELIVERY_MODE = {
  ONLINE: 1,
  OFFLINE: 2,
  ONDEMAND: 3
} as const;

export type DeliveryMode = ValueOf<typeof DELIVERY_MODE>;

export const SEMESTER_TYPE = {
  REGULAR: 'REGULAR',
  FLEXIBLE: 'FLEXIBLE',
} as const;

export type SemesterType = ValueOf<typeof SEMESTER_TYPE>;

export const EXPIRY_MODE = {
  NONE: 'none',
  DATE: 'date',
} as const;

export type ExpiryMode = ValueOf<typeof EXPIRY_MODE>;

export const CALENDAR_VIEW = {
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
} as const;

export type CalendarView = ValueOf<typeof CALENDAR_VIEW>;
export type CalendarDayMonthView = typeof CALENDAR_VIEW.DAY | typeof CALENDAR_VIEW.MONTH;

export const CALENDAR_SELECT_VIEW = {
  DATE: 'date',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
} as const;

export type CalendarSelectView = ValueOf<typeof CALENDAR_SELECT_VIEW>;

export const CALENDAR_SELECTION_MODE = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
} as const;

export type CalendarSelectionMode = ValueOf<typeof CALENDAR_SELECTION_MODE>;

export const CALENDAR_EVENT_TYPE = {
  LECTURE: 1,
  EVENT: 2,
} as const;

export type CalendarEventType = ValueOf<typeof CALENDAR_EVENT_TYPE>;

export const CALENDAR_EVENT_TYPE_OPTIONS: readonly CalendarEventType[] = [
  CALENDAR_EVENT_TYPE.LECTURE,
  CALENDAR_EVENT_TYPE.EVENT,
];

export const CALENDAR_LECTURE_MODE = {
  OFFLINE: 1,
  ON_DEMAND: 2,
  ONLINE: 3,
} as const;

export type CalendarLectureMode = ValueOf<typeof CALENDAR_LECTURE_MODE>;

export const CALENDAR_LECTURE_MODE_OPTIONS: readonly CalendarLectureMode[] = [
  CALENDAR_LECTURE_MODE.OFFLINE,
  CALENDAR_LECTURE_MODE.ON_DEMAND,
  CALENDAR_LECTURE_MODE.ONLINE,
];

export const CALENDAR_EVENT_DEFAULT_TIME = {
  START: '10:00',
  END: '12:00',
} as const;

export const CALENDAR_EVENT_REPEAT_DEFAULT_YEARS = 1 as const;

export const CALENDAR_EVENT_SCOPE = {
  SINGLE: 'SINGLE',
  FUTURE: 'FUTURE',
  ALL: 'ALL',
} as const;

export type CalendarEventScope = ValueOf<typeof CALENDAR_EVENT_SCOPE>;

export const CALENDAR_EVENT_SCOPE_OPTIONS: readonly CalendarEventScope[] = [
  CALENDAR_EVENT_SCOPE.SINGLE,
  CALENDAR_EVENT_SCOPE.FUTURE,
  CALENDAR_EVENT_SCOPE.ALL,
];

export const CALENDAR_DB_EVENT_TYPE = {
  CLASS: 'CLASS',
  EVENT: 'EVENT',
} as const;

export type CalendarDbEventType = ValueOf<typeof CALENDAR_DB_EVENT_TYPE>;

export const CALENDAR_DB_DELIVERY_TYPE = {
  ONLINE: 1,
  OFFLINE: 2,
  HYBRID: 3,
} as const;

export type CalendarDbDeliveryType = ValueOf<typeof CALENDAR_DB_DELIVERY_TYPE>;

export const CALENDAR_DB_RECURRENCE_TYPE = {
  NONE: 'NONE',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
} as const;

export type CalendarDbRecurrenceType = ValueOf<typeof CALENDAR_DB_RECURRENCE_TYPE>;

export const CALENDAR_DB_EVENT_STATUS = {
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
} as const;

export type CalendarDbEventStatus = ValueOf<typeof CALENDAR_DB_EVENT_STATUS>;

export const CALENDAR_DB_KEY_PREFIX = {
  SCHEDULE: 'SCH',
  EVENT: 'EVENT',
  USER: 'USER',
  DATE: 'DATE',
} as const;

export const CALENDAR_DB_DEFAULT = {
  SEMESTER_ID: 'SEMESTER_ID'
} as const;

export const CALENDAR_EVENT_DEFAULTS = {
  OFFLINE_PLACE: '教室2-1',
  DEFAULT_ASSIGNMENT: '介護ロボットについて（課題）.pdf',
  COURSE_CODE: 'KG202601',
  EVENT_CODE_PREFIX: 'EV',
  PARTICIPANT_GROUP_TEACHER: 'TEACHER',
  PARTICIPANT_GROUP_STUDENT: 'STUDENT',
} as const;

export const CALENDAR_REPEAT_DAY = {
  SUN: 'SUN',
  MON: 'MON',
  TUE: 'TUE',
  WED: 'WED',
  THU: 'THU',
  FRI: 'FRI',
  SAT: 'SAT',
} as const;

export type CalendarRepeatDay = ValueOf<typeof CALENDAR_REPEAT_DAY>;

export const CALENDAR_REPEAT_DAY_OPTIONS: readonly CalendarRepeatDay[] = [
  CALENDAR_REPEAT_DAY.SUN,
  CALENDAR_REPEAT_DAY.MON,
  CALENDAR_REPEAT_DAY.TUE,
  CALENDAR_REPEAT_DAY.WED,
  CALENDAR_REPEAT_DAY.THU,
  CALENDAR_REPEAT_DAY.FRI,
  CALENDAR_REPEAT_DAY.SAT,
];

export const APP_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  CLOSED: 'CLOSED',
} as const;

export const APP_GENDER = {
  NONE: 'NONE', 
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'OTHER',
} as const;

export type AppStatus = ValueOf<typeof APP_STATUS>;
export type ActiveInactiveStatus = typeof APP_STATUS.ACTIVE | typeof APP_STATUS.INACTIVE;
export type ActiveInactiveClosedStatus =
  | typeof APP_STATUS.ACTIVE
  | typeof APP_STATUS.INACTIVE
  | typeof APP_STATUS.CLOSED;

export const APP_GENDER_OPTIONS: readonly string[] = [
  APP_GENDER.MALE,
  APP_GENDER.FEMALE
];

export const ACTIVE_INACTIVE_OPTIONS: readonly ActiveInactiveStatus[] = [
  APP_STATUS.ACTIVE,
  APP_STATUS.INACTIVE,
];

export const ACTIVE_INACTIVE_CLOSED_OPTIONS: readonly ActiveInactiveClosedStatus[] = [
  APP_STATUS.ACTIVE,
  APP_STATUS.INACTIVE,
  // APP_STATUS.CLOSED,
];

export const DEVICE_STATUS = {
  AVAILABLE: 'DEVICE_STATUS_AVAILABLE',
  IN_USE: 'DEVICE_STATUS_IN_USE',
  UNAVAILABLE: 'DEVICE_STATUS_UNAVAILABLE',
} as const;

export type DeviceStatus = ValueOf<typeof DEVICE_STATUS>;

export const DEVICE_STATUS_OPTIONS: readonly DeviceStatus[] = [
  DEVICE_STATUS.AVAILABLE,
  DEVICE_STATUS.IN_USE,
  DEVICE_STATUS.UNAVAILABLE,
];

export const PROGRESS_STATUS = {
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  NOT_STARTED: 'NOT_STARTED',
} as const;

export type ProgressStatus = ValueOf<typeof PROGRESS_STATUS>;

export const APP_DEVICE_TYPE = {
  DEVICE_TYPE_CLASSROOM: 'DEVICE_TYPE_CLASSROOM',
  DEVICE_TYPE_AV: 'DEVICE_TYPE_AV',
} as const;

export const DEVICE_TYPE_OPTIONS: readonly string[] = [
  APP_DEVICE_TYPE.DEVICE_TYPE_CLASSROOM,
  APP_DEVICE_TYPE.DEVICE_TYPE_AV,
];


// Semester related constants
export const MONTH = {
  MONTH_1: 'MONTH_1',
  MONTH_2: 'MONTH_2',
  MONTH_3: 'MONTH_3',
  MONTH_4: 'MONTH_4',
  MONTH_5: 'MONTH_5',
  MONTH_6: 'MONTH_6',
  MONTH_7: 'MONTH_7',
  MONTH_8: 'MONTH_8',
  MONTH_9: 'MONTH_9',
  MONTH_10: 'MONTH_10',
  MONTH_11: 'MONTH_11',
  MONTH_12: 'MONTH_12',
} as const;

export const MONTH_OPTIONS: readonly string[] = [
  MONTH.MONTH_1,
  MONTH.MONTH_2,
  MONTH.MONTH_3,
  MONTH.MONTH_4,
  MONTH.MONTH_5,
  MONTH.MONTH_6,
  MONTH.MONTH_7,
  MONTH.MONTH_8,
  MONTH.MONTH_9,
  MONTH.MONTH_10,
  MONTH.MONTH_11,
  MONTH.MONTH_12,
];

export const YEAR = {
  YEAR_1: 'YEAR_1',
  YEAR_2: 'YEAR_2',
  YEAR_3: 'YEAR_3',
  YEAR_4: 'YEAR_4',
  YEAR_5: 'YEAR_5',
  YEAR_6: 'YEAR_6'
} as const;

export const YEAR_OPTIONS: readonly string[] = [
  YEAR.YEAR_1,
  YEAR.YEAR_2,
  YEAR.YEAR_3,
  YEAR.YEAR_4,
  YEAR.YEAR_5,
  YEAR.YEAR_6
];


export const SEMESTER_TYPE_OPTIONS: readonly SemesterType[] = [
  // SEMESTER_TYPE.REGULAR,
  SEMESTER_TYPE.FLEXIBLE,
];

export enum EventType {
  EVENT = 'EVENT',
  TEST = 'TEST',
  DEADLINE = 'DEADLINE',
  PRACTICE = 'PRACTICE'
}

export const MONTH_OPTIONS_SELECT = {
  MONTH_START: 'MONTH_START',
  MONTH_END: 'MONTH_END',
} as const;
